import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { buscarImoveisEmpreendimento } from '~/Services/Service';
import { customStylesSm, DropdownIndicator } from '~/Styles/reactSelectStyles';

export default function SelecaoAtivo(props) {
  var onSelectAtivo = props.onSelectAtivo;

  var empreendimentos = useSelector(function(state) {
    return state.empreendimentos;
  });

  var [empreendimentoSelecionado, setEmpreendimentoSelecionado] = useState(
    null
  );
  var [quadraSelecionada, setQuadraSelecionada] = useState(null);
  var [loteSelecionado, setLoteSelecionado] = useState(null);
  var [imoveis, setImoveis] = useState(null);
  var [loadingImoveis, setLoadingImoveis] = useState(false);

  var optionsEmpreendimentos = useMemo(
    function() {
      var lista =
        empreendimentos && empreendimentos.sort ? empreendimentos.sort : [];
      return lista.map(function(item) {
        return {
          value: item.codigo,
          label: item.codigo + ' - ' + item.descricao,
        };
      });
    },
    [empreendimentos]
  );

  var optionsQuadras = useMemo(
    function() {
      if (!imoveis) return [];
      var vistas = {};
      var lista = [];
      imoveis.forEach(function(item) {
        var q = item.quadra;
        if (q != null && !vistas[q]) {
          vistas[q] = true;
          lista.push(q);
        }
      });
      lista.sort(function(a, b) {
        return String(a).localeCompare(String(b), undefined, { numeric: true });
      });
      return lista.map(function(q) {
        return { value: q, label: 'Quadra ' + q };
      });
    },
    [imoveis]
  );

  var optionsLotes = useMemo(
    function() {
      if (!imoveis || !quadraSelecionada) return [];
      return imoveis
        .filter(function(item) {
          return item.quadra === quadraSelecionada.value;
        })
        .sort(function(a, b) {
          return String(a.lote).localeCompare(String(b.lote), undefined, {
            numeric: true,
          });
        })
        .map(function(item) {
          return {
            value: item.codigo,
            label: 'Lote ' + item.lote,
          };
        });
    },
    [imoveis, quadraSelecionada]
  );

  var carregarImoveis = useCallback(async function(codigoFamilia) {
    try {
      setLoadingImoveis(true);
      var response = await buscarImoveisEmpreendimento(codigoFamilia);
      setImoveis(response || []);
    } catch (err) {
      toast.error(
        (err && err.message) || 'Falha ao carregar imóveis do empreendimento.'
      );
      setImoveis([]);
    } finally {
      setLoadingImoveis(false);
    }
  }, []);

  var onContextoChange = props.onContextoChange;

  var handleEmpreendimentoChange = useCallback(
    function(option) {
      setEmpreendimentoSelecionado(option);
      setQuadraSelecionada(null);
      setLoteSelecionado(null);
      setImoveis(null);
      if (onSelectAtivo) onSelectAtivo(null);
      if (onContextoChange) onContextoChange({
        codigoFamilia: option ? option.value : null,
        empreendimentoLabel: option ? option.label : null,
        quadra: null, lote: null, codigoAtivo: null,
      });
      if (option) {
        carregarImoveis(option.value);
      }
    },
    [carregarImoveis, onSelectAtivo, onContextoChange]
  );

  var handleQuadraChange = useCallback(
    function(option) {
      setQuadraSelecionada(option);
      setLoteSelecionado(null);
      if (onSelectAtivo) onSelectAtivo(null);
      if (onContextoChange) onContextoChange({
        codigoFamilia: empreendimentoSelecionado ? empreendimentoSelecionado.value : null,
        empreendimentoLabel: empreendimentoSelecionado ? empreendimentoSelecionado.label : null,
        quadra: option ? option.value : null,
        lote: null, codigoAtivo: null,
      });
    },
    [onSelectAtivo, onContextoChange, empreendimentoSelecionado]
  );

  var handleLoteChange = useCallback(
    function(option) {
      setLoteSelecionado(option);
      if (onSelectAtivo) onSelectAtivo(option ? option.value : null);
      if (onContextoChange) onContextoChange({
        codigoFamilia: empreendimentoSelecionado ? empreendimentoSelecionado.value : null,
        empreendimentoLabel: empreendimentoSelecionado ? empreendimentoSelecionado.label : null,
        quadra: quadraSelecionada ? quadraSelecionada.value : null,
        lote: option ? option.label : null,
        codigoAtivo: option ? option.value : null,
      });
    },
    [onSelectAtivo, onContextoChange, empreendimentoSelecionado, quadraSelecionada]
  );

  useEffect(
    function() {
      return function() {
        if (onSelectAtivo) onSelectAtivo(null);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    []
  );

  var selectStyles = Object.assign({}, customStylesSm, {
    menuPortal: function(base) {
      return Object.assign({}, base, { zIndex: 99999 });
    },
  });

  var filterByLabel = useCallback(function(option, input) {
    if (!input) return true;
    return option.label.toLowerCase().includes(input.toLowerCase());
  }, []);

  return (
    <Card className="mb-2">
      <Card.Body className="py-2">
        <h6 className="mb-2 text-primary">
          <strong>Seleção de Ativo</strong>
        </h6>
        <Row>
          <Col md={4}>
            <Form.Group className="mb-2">
              <Form.Label className="small mb-1">Empreendimento</Form.Label>
              <Select
                placeholder="Selecione o empreendimento..."
                options={optionsEmpreendimentos}
                value={empreendimentoSelecionado}
                onChange={handleEmpreendimentoChange}
                styles={selectStyles}
                components={{ DropdownIndicator: DropdownIndicator }}
                filterOption={filterByLabel}
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-2">
              <Form.Label className="small mb-1">Quadra</Form.Label>
              <Select
                placeholder={
                  loadingImoveis ? 'Carregando...' : 'Selecione a quadra...'
                }
                options={optionsQuadras}
                value={quadraSelecionada}
                onChange={handleQuadraChange}
                styles={selectStyles}
                components={{ DropdownIndicator: DropdownIndicator }}
                filterOption={filterByLabel}
                isClearable
                isDisabled={
                  !empreendimentoSelecionado ||
                  loadingImoveis ||
                  optionsQuadras.length === 0
                }
                isLoading={loadingImoveis}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-2">
              <Form.Label className="small mb-1">Lote</Form.Label>
              <Select
                placeholder="Selecione o lote..."
                options={optionsLotes}
                value={loteSelecionado}
                onChange={handleLoteChange}
                styles={selectStyles}
                components={{ DropdownIndicator: DropdownIndicator }}
                filterOption={filterByLabel}
                isClearable
                isDisabled={!quadraSelecionada || optionsLotes.length === 0}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </Form.Group>
          </Col>
        </Row>
        {loadingImoveis && (
          <div className="text-center mt-2">
            <Spinner animation="border" size="sm" variant="primary" />
            <span className="ml-2">Carregando imóveis do empreendimento...</span>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}
